import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/Button';
import { Dialog } from '@/components/ui/Dialog';
import { CustomerFormDialog } from '@/components/customers/CustomerFormDialog';
import type { CustomerDetail } from '@/hooks/useCustomers';
import type { UnmappedClientGroup } from '@/types/api';

/**
 * Zwischenschritt vor "Kunde anlegen + verknuepfen" auf der Mapping-Seite.
 *
 * Am 04.09.2026 hat der Betreiber für den Invoice-Ninja-Client
 * "Winkler Hafner Rechtsanwaelte GmbH" einen neuen Kunden angelegt, obwohl
 * "Winkler Law" schon existierte. Das Backend kannte die Ähnlichkeit
 * (suggestions), der Dialog hat sie nicht gezeigt. Ergebnis: eine Dublette,
 * die anschließend gelöscht — und über den Deep-Link weiter wie ein
 * aktiver Kunde angezeigt — wurde.
 *
 * Deshalb: sobald das Backend Vorschläge liefert, muss der Betreiber sie
 * sehen und bewusst "keiner passt" sagen, bevor das Formular aufgeht.
 */

interface CreateOrLinkCustomerDialogProps {
  /** null = geschlossen. */
  client: UnmappedClientGroup | null;
  onClose: () => void;
  /** Bestehenden Kunden verknüpfen (ruft die map-client-Mutation des Aufrufers). */
  onLink: (customerId: string, customerName: string) => void;
  /** Neuer Kunde wurde angelegt. */
  onCreated: (customer: CustomerDetail) => void;
}

export function CreateOrLinkCustomerDialog({
  client,
  onClose,
  onLink,
  onCreated,
}: CreateOrLinkCustomerDialogProps): JSX.Element {
  const [schritt, setSchritt] = useState<'vorschlaege' | 'formular'>('vorschlaege');
  const [keinerPasst, setKeinerPasst] = useState(false);

  useEffect(() => {
    if (client === null) return;
    // Ohne Vorschläge ist der Zwischenschritt sinnlos und wird übersprungen.
    setSchritt(client.suggestions.length > 0 ? 'vorschlaege' : 'formular');
    setKeinerPasst(false);
  }, [client]);

  if (client === null) {
    return <CustomerFormDialog open={false} onClose={onClose} />;
  }

  // Nur EIN natives <dialog> gleichzeitig — sonst stapeln sie sich unschön.
  const zeigtFormular = schritt === 'formular' || client.suggestions.length === 0;

  if (zeigtFormular) {
    return (
      <CustomerFormDialog
        open
        onClose={onClose}
        prefill={{
          name: client.client_name,
          invoice_ninja_client_id: client.invoice_ninja_client_id,
        }}
        onSuccess={onCreated}
      />
    );
  }

  return (
    <Dialog
      open
      onClose={onClose}
      title="Ähnliche Kunden"
      description={`Für "${client.client_name}" gibt es schon ähnliche Kunden im Wartungstool.`}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Abbrechen
          </Button>
          <Button
            variant="secondary"
            data-testid="no-matching-customer"
            onClick={() => setKeinerPasst(true)}
          >
            Kein passender Kunde
          </Button>
          <Button
            data-testid="create-anyway"
            disabled={!keinerPasst}
            onClick={() => setSchritt('formular')}
          >
            Trotzdem neu anlegen
          </Button>
        </>
      }
    >
      <ul aria-label="Ähnliche Kunden" className="flex flex-col gap-2">
        {client.suggestions.map((s) => (
          <li
            key={s.customer_id}
            className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-border p-2"
          >
            <div className="min-w-0">
              <p className="font-medium">{s.customer_name}</p>
              <p className="font-mono text-xs text-muted">{s.customer_slug}</p>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted">{Math.round(s.score * 100)}%</span>
              <Button
                size="sm"
                data-testid={`link-suggestion-${s.customer_id}`}
                onClick={() => {
                  onLink(s.customer_id, s.customer_name);
                  onClose();
                }}
              >
                Verknüpfen
              </Button>
            </div>
          </li>
        ))}
      </ul>
      <p className="mt-3 text-sm text-muted">
        Nur wenn wirklich keiner passt, einen neuen Kunden anlegen — eine Dublette
        kostet später mehr Zeit als die Prüfung jetzt.
      </p>
    </Dialog>
  );
}
